<template>
  <nav class="sticky top-0 z-50 backdrop-blur-xl bg-gray-900/80 border-b border-white/10">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <!-- Left side -->
        <div class="flex items-center space-x-8">
          <!-- Logo -->
          <router-link to="/" class="flex items-center">
            <img 
              src="../assets/logo.png" 
              alt="SEO Analyzer Logo" 
              class="h-10 w-auto"
            />
            <span class="sr-only">SEO Analyzer</span>
          </router-link>

          <!-- Navigation Links -->
          <div class="hidden sm:flex sm:items-center sm:space-x-6">
            <template v-if="user">
              <router-link
                v-if="isAdmin"
                to="/admin"
                class="nav-link group"
                :class="{ 'text-primary-400': $route.path === '/admin' }"
              >
                <div class="flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon w-5 h-5 transition-colors duration-200" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                <span>Admin</span>
                </div>
              </router-link>
              <router-link
                to="/dashboard"
                class="nav-link group"
                :class="{ 'text-primary-400': $route.path === '/dashboard' }"
              >
                <div class="flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon w-5 h-5 transition-colors duration-200" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V4zM8 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1H9a1 1 0 01-1-1V4zM15 3a1 1 0 00-1 1v12a1 1 0 001 1h2a1 1 0 001-1V4a1 1 0 00-1-1h-2z" />
                </svg>
                <span>Dashboard</span>
                </div>
              </router-link>
              <router-link
                to="/generated-content"
                class="nav-link group"
                :class="{ 'text-primary-400': $route.name === 'generated-content' }"
              >
                <div class="flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon w-5 h-5 transition-colors duration-200" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 8v2a1 1 0 00.293.707l.646.647a1 1 0 001.414-1.414l-2 3.293a1 1 0 00-.414 1.414l-.757.758A1 1 0 0010 17h4a1 1 0 001-1V7a1 1 0 00-.293-.707L15 6.586A1 1 0 0010 2z" clip-rule="evenodd" />
                </svg>
                <span>Generated Content</span>
                </div>
              </router-link>
            </template>

            <router-link
              to="/pricing"
              class="nav-link group"
              :class="{ 'text-primary-400': $route.path === '/pricing' }"
            >
              <div class="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="icon w-5 h-5 transition-colors duration-200" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
              </svg>
              <span>Pricing</span>
              </div>
            </router-link>
          </div>
        </div>

        <!-- Right side -->
        <div class="flex items-center">
          <!-- Profile dropdown - only show for authenticated users -->
          <template v-if="user">
            <div class="relative profile-dropdown">
              <button
                @click="toggleProfileMenu"
                class="flex items-center space-x-3 focus:outline-none"
              >
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white shadow-lg hover:scale-105 transition-transform duration-200">
                  <span class="text-sm font-medium">
                    {{ userInitials }}
                  </span>
                </div>
              </button>

              <!-- Dropdown menu -->
              <div 
                v-if="showProfileMenu" 
                class="absolute right-0 mt-3 w-56 rounded-xl shadow-lg py-2 backdrop-blur-xl bg-gray-900/90 border border-white/10 ring-1 ring-black ring-opacity-5 z-50"
              >
                <!-- Mobile Navigation Links - Only show on small screens when logged in -->
                <template v-if="user">
                  <div class="sm:hidden">
                    <router-link
                      v-if="isAdmin"
                      to="/admin"
                      class="mobile-nav-link"
                      @click="closeProfileMenu"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="icon-sm w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                      <span>Admin</span>
                    </router-link>
                    <router-link
                      to="/dashboard"
                      class="mobile-nav-link"
                      @click="closeProfileMenu"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="icon-sm w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M2 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V4zM8 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1H9a1 1 0 01-1-1V4zM15 3a1 1 0 00-1 1v12a1 1 0 001 1h2a1 1 0 001-1V4a1 1 0 00-1-1h-2z" />
                      </svg>
                      <span>Dashboard</span>
                    </router-link>
                    <router-link
                      to="/generated-content"
                      class="mobile-nav-link"
                      @click="closeProfileMenu"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="icon-sm w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 8v2a1 1 0 00.293.707l.646.647a1 1 0 001.414-1.414l-2 3.293a1 1 0 00-.414 1.414l-.757.758A1 1 0 0010 17h4a1 1 0 001-1V7a1 1 0 00-.293-.707L15 6.586A1 1 0 0010 2z" clip-rule="evenodd" />
                      </svg>
                      <span>Generated Content</span>
                    </router-link>
                    <router-link
                      to="/pricing"
                      class="mobile-nav-link"
                      @click="closeProfileMenu"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="icon-sm w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                      </svg>
                      <span>Pricing</span>
                    </router-link>
                    <div class="border-t border-white/10 my-2"></div>
                  </div>
                </template>
                <!-- Regular Profile Menu Items -->
                <router-link 
                  to="/profile" 
                  class="mobile-nav-link"
                  @click="closeProfileMenu"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon-sm w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clip-rule="evenodd" />
                  </svg>
                  <span>Profile</span>
                </router-link>
                <router-link 
                  to="/account" 
                  class="mobile-nav-link"
                  @click="closeProfileMenu"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon-sm w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
                  </svg>
                  <span>Account</span>
                </router-link>
                <router-link
                  to="/connections"
                  class="mobile-nav-link"
                  @click="closeProfileMenu"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon-sm w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 8v2a1 1 0 00.293.707l.646.647a1 1 0 001.414-1.414l-2 3.293a1 1 0 00-.414 1.414l-.757.758A1 1 0 0010 17h4a1 1 0 001-1V7a1 1 0 00-.293-.707L15 6.586A1 1 0 0010 2z" clip-rule="evenodd" />
                  </svg>
                  <span>Connections</span>
                </router-link>
                <button 
                  @click="handleLogout" 
                  class="mobile-nav-link w-full text-left"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon-sm w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </template>
          <!-- Login button for non-authenticated users -->
          <router-link
            v-else
            to="/login"
            class="group relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-xl text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 transform transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="icon-sm w-4 h-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            <span>Login</span>
          </router-link>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useSubscriptionStore } from '../stores/subscription'

const router = useRouter()
const authStore = useAuthStore()
const subscriptionStore = useSubscriptionStore()
const showProfileMenu = ref(false)

const user = computed(() => authStore.user)
const currentTier = computed(() => subscriptionStore.currentTier)

const userInitials = computed(() => {
  if (!user.value?.email) return '?'
  return user.value.email.substring(0, 2).toUpperCase()
})

const isAdmin = computed(() => {
  return subscriptionStore.currentTier === 'admin'
})

function toggleProfileMenu() {
  showProfileMenu.value = !showProfileMenu.value
}

function closeProfileMenu() {
  showProfileMenu.value = false
}

async function handleLogout() {
  try {
    await authStore.signOut()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
  }
}

// Close dropdown when clicking outside
function handleClickOutside(event) {
  const dropdown = document.querySelector('.profile-dropdown')
  if (dropdown && !dropdown.contains(event.target)) {
    showProfileMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.nav-link {
  @apply relative flex items-center text-gray-300 hover:text-white transition-colors duration-200 py-1;
}

.mobile-nav-link {
  @apply flex items-center space-x-2 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors duration-200;
}

.icon {
  @apply text-gray-300 group-hover:text-white transition-colors duration-200;
}

.icon-sm {
  @apply text-gray-300 transition-colors duration-200;
}
</style>
